import React, { useState, useEffect } from 'react';
import { Button, Table } from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import { useGetAccessLogs } from 'services/2n/access-logs/get-access-logs';
import { CheckWithBackground } from 'assets/icons/CheckWithBackground';
import { MinusWithBackground } from 'assets/icons/MinusWithBackground';
import { FilterListIcon } from 'assets/icons/FilterListIcon';
import Loader from 'components/loaders/Loader';
import { Brikk } from 'components/Brikk';
import { AccessLogsFilterModal } from './components/AccessLogsFilterModal';
import { AccessLogsTableHeader } from './components/AccessLogsTableHeader';
import { AccessLogTableRow } from './components/AccessLogsTableRow';
import styles from './styles.module.scss';

const getRowFieldValue = (row, column) => {
  if (column === 'category') {
    if (row.category.type === CheckWithBackground) return 'Granted';
    if (row.category.type === MinusWithBackground) return 'Denied';
    return '';
  }
  if (column === 'credentials') {
    if (typeof row.credentials.type === 'string') return row.credentials.type;
    return Object.keys(row.credentials.type || {})
      .find(key => row.credentials.type[key] === 'valid') || '';
  }
  return row[column] || '';
};

const matchesFilter = (row, filter) => {
  if (!filter) return true;
  if (Array.isArray(filter)) {
    return filter.filter(f => matchesFilter(row, f));
  }
  const { column, operator, value } = filter;
  if (!column) return true;

  const cellValue = getRowFieldValue(row, column);
  const cellStr = String(cellValue).toLowerCase();

  const matches = (v) => {
    const vStr = String(v).toLowerCase();
    if (column === 'user') {
      return vStr === String(row.userId).toLowerCase() || vStr === String(row.user).toLowerCase();
    }
    if (column === 'zone') {
      return vStr === String(row.zoneId).toLowerCase() || vStr === String(row.zone).toLowerCase();
    }
    return cellStr === vStr;
  };

  if (operator === 'is') {
    if (!value) return true;
    if (Array.isArray(value)) {
      if (value.length === 0) return true;
      return value.filter(matches);
    }
    return matches(value);
  }
  if (operator === 'is any of') {
    if (!value || value.length === 0) return true;
    return value.filter(matches);
  }
  if (operator === 'is after') {
    if (!value) return true;
    const cellDate = new Date(cellValue);
    const filterDate = new Date(value);
    return !Number.isNaN(cellDate.getTime()) && !Number.isNaN(filterDate.getTime()) ? cellDate > filterDate : true;
  }
  if (operator === 'is before') {
    if (!value) return true;
    const cellDate = new Date(cellValue);
    const filterDate = new Date(value);
    return !Number.isNaN(cellDate.getTime()) && !Number.isNaN(filterDate.getTime()) ? cellDate < filterDate : true;
  }

  return true;
};

export const AccessLogs = () => {
  const intl = useIntl();
  const [activePage, setActivePage] = useState(1);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState({ column: 'category', operator: 'is', value: null });
  const [accumulatedLogs, setAccumulatedLogs] = useState([]);

  const filtersArray = Array.isArray(activeFilter) ? activeFilter : [activeFilter];

  const appliedFiltersCount = filtersArray.filter(f => {
    if (f.value === null || f.value === undefined) return false;
    if (Array.isArray(f.value)) return f.value.length > 0;
    if (typeof f.value === 'string') return f.value.trim() !== '';
    return true;
  }).length;

  const getFilterParam = (columnName) => {
    const filter = filtersArray.find(f => f.column === columnName);
    if (!filter) return undefined;
    if (Array.isArray(filter.value)) {
      if (columnName !== 'time') {
        return filter.value.length > 0 ? filter.value : undefined;
      }
      return filter.value.length > 0 ? filter.value[0] : undefined;
    }
    return filter.value || undefined;
  };

  const categoryParam = getFilterParam('category');
  const userParam = getFilterParam('user');
  const credentialsParam = getFilterParam('credentials');
  const zoneParam = getFilterParam('zone');

  const timeFilters = filtersArray.filter(f => f.column === 'time');
  const fromParam = timeFilters.find(f => f.operator === 'is after')?.value || undefined;
  const toParam = timeFilters.find(f => f.operator === 'is before')?.value || undefined;

  const { accessLogs, isFetching, isLoading } = useGetAccessLogs(
    activePage,
    categoryParam,
    userParam,
    credentialsParam,
    zoneParam,
    fromParam,
    toParam,
  );

  useEffect(() => {
    if (activePage === 1) {
      setAccumulatedLogs(accessLogs || []);
    } else if (accessLogs && accessLogs.length > 0) {
      setAccumulatedLogs(prev => {
        const existingIds = new Set(prev.map(row => row.id || `${row.user || ''}/${row.time}`));
        const uniqueNewLogs = accessLogs.filter(row => !existingIds.has(row.id || `${row.user || ''}/${row.time}`));
        return [...prev, ...uniqueNewLogs];
      });
    }
  }, [accessLogs, activePage]);

  const getDaysCount = (logs) => {
    if (!logs || logs.length === 0) return 0;
    const dates = logs
      .map(log => {
        const d = new Date(log.time);
        if (Number.isNaN(d.getTime())) return null;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
      })
      .filter(Boolean);

    if (dates.length === 0) return 0;

    const minDate = new Date(Math.min(...dates));
    const today = new Date();
    const maxDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffTime = Math.abs(maxDate - minDate);
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  const handleTitleClick = (index) => {
    setActiveIndex(activeIndex === index ? -1 : index);
  };

  const filteredLogs = filtersArray.length > 0
    ? accumulatedLogs.filter(row => filtersArray.filter(filter => matchesFilter(row, filter)))
    : accumulatedLogs;

  const handleApplyFilter = (filter) => {
    setActiveFilter(filter);
    setActivePage(1);
  };

  const hasFilteredLogs = filteredLogs.length > 0;
  const showLoader = activePage === 1 && (isFetching || isLoading);

  return (
    <>
      <div className={styles.stickyHeader}>
        <Brikk.Heading heading="accessLogs" />
        <div className={styles.accessLogsFilterContainer}>
          <Button
            onClick={() => setIsFilterModalOpen(true)}
            icon
          >
            <FilterListIcon />
            {appliedFiltersCount > 0
             && intl.formatMessage({ id: 'filtersApplied' }, { count: <strong>{appliedFiltersCount}</strong> })}

          </Button>
        </div>
      </div>

      {isFilterModalOpen && (
        <AccessLogsFilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          currentFilter={activeFilter}
          onApply={handleApplyFilter}
        />
      )}

      {showLoader ? (
        <div className={styles.container}><Loader /></div>
      ) : (
        <div className={styles.tableScrollContainer}>
          <Table
            striped
            size="small"
            compact="very"
            style={{ margin: 0 }}
          >
            <AccessLogsTableHeader className={styles.stickyTableHeader} />

            <Table.Body>
              {hasFilteredLogs ? (
                filteredLogs.map((row, index) => (
                  <AccessLogTableRow
                    key={row.id || `${row.user || index}/${index}`}
                    row={row}
                    index={index}
                    activeIndex={activeIndex}
                    onTitleClick={handleTitleClick}
                  />
                ))
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={8}>
                    <div className={styles.emptyState}>
                      {intl.formatMessage({ id: 'noAccessLogs' })}
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>

            {hasFilteredLogs && (
            <Table.Footer className={styles.stickyFooter}>
              <Table.Row>
                <Table.HeaderCell
                  colSpan={8}
                  className={styles.pagination}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '10px 0' }}>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>
                      {intl.formatMessage(
                        {
                          id: 'dataDisplayedForLastDays',
                          defaultMessage: 'Data is displayed from last {count, plural, one {day} other {{count} days}}',
                        },
                        { count: getDaysCount(filteredLogs) },
                      )}
                    </p>
                    <Button
                      primary
                      loading={isFetching}
                      onClick={() => {
                        setActivePage(prev => prev + 1);
                        setActiveIndex(-1);
                      }}
                    >
                      {intl.formatMessage({ id: 'loadMore', defaultMessage: 'Load More' })}
                    </Button>

                  </div>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
            )}

          </Table>
        </div>
      )}
    </>
  );
};
